/** @jsxImportSource @emotion/react */
import { pluralize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { type ReactNode, useMemo, useState } from 'react';

import { kaiPartialIdPresenter } from '@kaiizen/kai-presenters';
import type {
  ApiScan,
  ScopeReportLineItem,
} from '@kaiizen/kai-pro-api';
import { useScanExcelExport, useScanPdfExport, useScopeReportData, useTaskModifierDisplayNames } from '@kaiizen/kai-pro-api-react-state';
import { formatCurrency, getReportData, type ReportType } from '@kaiizen/kai-reports';

import { ANALYTICS_EVENTS } from '@/Client/analytics';
import { GalleryPhotoModal } from '@/Client/Partials/FocusedPhotoGallery/GalleryPhotoModal';
import { useScopeReportDownloader } from '@/Client/Partials/ScopePrintPreview/useScopeReportDownloader';
import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { useDropdownMenu, ZoomableImageViewer } from '@/Client/Widgets';
import { styleguideFontFamily, styleguideLetterSpacing, styleguideSpacing } from '@kaiizen/kai-shiki/core';
import { CloseIcon } from '@kaiizen/kai-shiki/react';
import { formatArtifactDate, type HistoricalArtifactDocumentType } from './FocusedArtifactPanelLib';
import {
  buildHistoricalArtifactRooms,
  type HistoricalArtifactPhoto,
  type HistoricalArtifactPhotoGalleryGroup,
  type HistoricalArtifactTask,
} from './HistoricalScopeArtifactDocumentLib';
import { useArtifactDocumentPrint } from './useArtifactDocumentPrint';

const COLORS = styleguideColors;
const FONT_FAMILY = styleguideFontFamily;
const FONT_SIZE = styleguideFontSize;
const FONT_WEIGHT = styleguideFontWeight;
const LETTER_SPACING = styleguideLetterSpacing;
const SPACING = styleguideSpacing;
const DOCUMENT_TABLE_MIN_WIDTH = 740;
const AMOUNT_CHANGE_CALLOUT_WIDTH = SPACING.s8 * 3;

const VIEWPORT_STYLE = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${SPACING.s5}px;

  @media (max-width: 760px) {
    padding: 0;
  }

  @media print {
    overflow: visible;
    padding: 0;
    background: ${COLORS.white};
  }
`;

const DOCUMENT_STYLE = css`
  label: DOCUMENT_STYLE;
  width: min(100%, 1120px);
  margin: 0 auto ${SPACING.s8}px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  box-shadow: 0 14px 32px rgba(26, 26, 26, 0.08);

  @media (max-width: 760px) {
    width: 100%;
    margin: 0;
    border-right: 0;
    border-left: 0;
    box-shadow: none;
  }

  @media print {
    width: 100%;
    margin: 0;
    border: 0;
    box-shadow: none;
  }
`;

const DOCUMENT_FRAME_STYLE = css`
  label: DOCUMENT_FRAME_STYLE;
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.s5}px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 760px) {
    flex-direction: column;
    gap: ${SPACING.s3}px;
  }

  @media print {
    display: block;
    width: 100%;
  }
`;

const DOCUMENT_FRAME_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE = css`
  label: DOCUMENT_FRAME_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE;
  display: grid;
  grid-template-columns: minmax(${DOCUMENT_TABLE_MIN_WIDTH}px, 1fr) ${AMOUNT_CHANGE_CALLOUT_WIDTH}px;
  min-width: calc(${DOCUMENT_TABLE_MIN_WIDTH}px + ${AMOUNT_CHANGE_CALLOUT_WIDTH}px + ${SPACING.s5}px);

  @media (max-width: 760px) {
    display: flex;
    min-width: 0;
  }

  @media print {
    display: block;
    min-width: 0;
  }
`;

const DOCUMENT_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE = css`
  label: DOCUMENT_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE;
  width: 100%;
  min-width: 0;
  margin: 0;
`;

const AMOUNT_CHANGE_CALLOUT_RAIL_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_RAIL_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s3}px;
  width: ${AMOUNT_CHANGE_CALLOUT_WIDTH}px;
  padding-top: ${SPACING.s8 * 3 + SPACING.s4}px;

  @media (max-width: 760px), print {
    display: none;
  }
`;

const TOOLBAR_STYLE = css`
  display: flex;
  align-items: center;
  gap: ${SPACING.s3}px;
  min-height: 52px;
  padding: 0 ${SPACING.s5}px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: ${SPACING.s3}px ${SPACING.s4}px;
  }

  @media print {
    display: none;
  }
`;

const TOOLBAR_BACK_STYLE = css`
  flex: 0 0 auto;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const TOOLBAR_DIVIDER_STYLE = css`
  width: 1px;
  height: 20px;
  background: ${COLORS.stroke};
`;

const TOOLBAR_TITLE_STYLE = css`
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
`;

const TOOLBAR_META_STYLE = css`
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const TOOLBAR_STATUS_STYLE = css`
  label: TOOLBAR_STATUS_STYLE;
  display: flex;
  align-items: center;

  > span {
    align-self: center;
  }
`;

const TOOLBAR_ACTIONS_STYLE = css`
  label: TOOLBAR_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: ${SPACING.s2}px;
  margin-left: auto;
`;

const TOOLBAR_PRINT_STYLE = css`
  label: TOOLBAR_PRINT_STYLE;
  border: 1px solid ${COLORS.ink};
  padding: ${SPACING.s2}px ${SPACING.s3}px;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
  }
`;

const TOOLBAR_EXPORT_CONTAINER_STYLE = css`
  position: relative;
`;

const TOOLBAR_EXPORT_MENU_STYLE = css`
  position: absolute;
  top: calc(100% + ${SPACING.s2}px);
  right: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-width: max-content;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

const TOOLBAR_EXPORT_MENU_ITEM_STYLE = css`
  width: 100%;
  border: 0;
  border-bottom: 1px solid ${COLORS.stroke};
  padding: ${SPACING.s2}px ${SPACING.s3}px;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:last-of-type {
    border-bottom: 0;
  }

  &:hover,
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const HEADER_STYLE = css`
  display: flex;
  justify-content: space-between;
  gap: ${SPACING.s7}px;
  padding: ${SPACING.s8}px ${SPACING.s7}px ${SPACING.s7}px;
  border-bottom: 1px solid ${COLORS.stroke};

  @media (max-width: 760px) {
    flex-direction: column;
    gap: ${SPACING.s5}px;
    padding: ${SPACING.s6}px ${SPACING.s4}px;
  }
`;

const EYEBROW_STYLE = css`
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoStageNum)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const DOCUMENT_TITLE_STYLE = css`
  margin-top: ${SPACING.s3}px;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displayLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.08;
`;

const DOCUMENT_SUBTITLE_STYLE = css`
  margin-top: ${SPACING.s2}px;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  line-height: 1.45;
`;

const DOCUMENT_META_STYLE = css`
  display: grid;
  align-content: start;
  gap: ${SPACING.s4}px;
  min-width: 150px;
  text-align: right;

  @media (max-width: 760px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    min-width: 0;
    text-align: left;
  }
`;

const DOCUMENT_META_LABEL_STYLE = css`
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const DOCUMENT_META_VALUE_STYLE = css`
  margin-top: ${SPACING.s1}px;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  line-height: 1.3;
`;

const TABLE_WRAP_STYLE = css`
  overflow-x: auto;

  @media print {
    overflow: visible;
  }
`;

const TABLE_STYLE = css`
  width: 100%;
  min-width: ${DOCUMENT_TABLE_MIN_WIDTH}px;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    height: 48px;
    padding: 0 ${SPACING.s3}px;
    border-bottom: 1px solid ${COLORS.stroke};
    background: ${COLORS.paper};
    color: ${COLORS.textTertiary};
    font-family: ${FONT_FAMILY.mono};
    font-size: ${pxToRem(FONT_SIZE.monoMicro)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: ${LETTER_SPACING.monoCaption};
    text-align: left;
    text-transform: uppercase;
  }

  td {
    vertical-align: top;
  }

  @media print {
    min-width: 0;
  }
`;

const ID_COLUMN_STYLE = css`
  label: ID_COLUMN_STYLE;
  width: 96px;
  padding-left: ${SPACING.s5}px;
`;

const ITEM_COLUMN_STYLE = css`
  label: ITEM_COLUMN_STYLE;
  width: auto;
`;

const CONTRACTOR_COLUMN_STYLE = css`
  label: CONTRACTOR_COLUMN_STYLE;
  width: 132px;
`;

const QUANTITY_COLUMN_STYLE = css`
  label: QUANTITY_COLUMN_STYLE;
  width: 78px;
`;

const RATE_COLUMN_STYLE = css`
  label: RATE_COLUMN_STYLE;
  width: 78px;
`;

const AMOUNT_COLUMN_STYLE = css`
  label: AMOUNT_COLUMN_STYLE;
  width: 96px;
  padding-right: ${SPACING.s5}px;
`;

const NUMERIC_COLUMN_STYLE = css`
  label: NUMERIC_COLUMN_STYLE;
  white-space: nowrap;
  text-align: right !important;
`;

const GROUP_ROW_STYLE = css`
  label: GROUP_ROW_STYLE;
  td {
    padding: ${SPACING.s4}px ${SPACING.s5}px;
    border-bottom: 1px solid ${COLORS.ink};
    background: ${COLORS.white};
  }
`;

const GROUP_NAME_STYLE = css`
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const GROUP_META_STYLE = css`
  margin-left: ${SPACING.s3}px;
  color: ${COLORS.textTertiary};
`;

const GROUP_NOTE_ROW_STYLE = css`
  td {
    padding: ${SPACING.s3}px ${SPACING.s5}px;
    border-bottom: 1px solid ${COLORS.stroke};
    color: ${COLORS.textSecondary};
    font-family: ${FONT_FAMILY.body};
    font-size: ${pxToRem(FONT_SIZE.bodySmall)};
    line-height: 1.5;
  }
`;

const SCOPE_NOTES_STYLE = css`
  label: SCOPE_NOTES_STYLE;
  margin: 0 ${SPACING.s5}px ${SPACING.s4}px;
  padding: ${SPACING.s3}px 0;
  border-bottom: 1px solid ${COLORS.stroke};
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.5;
  white-space: pre-wrap;
`;

const TASK_ROW_STYLE = css`
  label: TASK_ROW_STYLE;
  td {
    padding: ${SPACING.s4}px ${SPACING.s3}px ${SPACING.s2}px;
  }
`;

const TASK_ID_STYLE = css`
  label: TASK_ID_STYLE;
  padding-left: ${SPACING.s5}px;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  white-space: nowrap;
`;

const TASK_TITLE_STYLE = css`
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.35;
`;

const TASK_DESCRIPTION_STYLE = css`
  margin-top: ${SPACING.s2}px;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  line-height: 1.55;
`;

const CONTRACTOR_STYLE = css`
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.4;
`;

const UNASSIGNED_CONTRACTOR_STYLE = css`
  color: ${COLORS.error};
  font-style: italic;
`;

const METRIC_STYLE = css`
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.4;
`;

const AMOUNT_STYLE = css`
  label: AMOUNT_STYLE;
  padding-right: ${SPACING.s5}px;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.4;
`;

const PREVIOUS_AMOUNT_STYLE = css`
  display: block;
  color: ${COLORS.textTertiary};
  font-weight: ${FONT_WEIGHT.regular};
  text-decoration: line-through;
`;

const EDITABLE_AMOUNT_INPUT_STYLE = css`
  label: EDITABLE_AMOUNT_INPUT_STYLE;
  width: 100%;
  min-width: 0;
  border: 1px solid ${COLORS.accent};
  padding: ${SPACING.s1}px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.4;
  text-align: right;
  cursor: text;

  &:hover {
    background: ${COLORS.wash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ink};
  }
`;

const AMOUNT_CHANGE_CALLOUT_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_STYLE;
  width: ${AMOUNT_CHANGE_CALLOUT_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: baseline;
  gap: ${SPACING.s2}px;
  border: 1px solid ${COLORS.stroke};
  border-left: 3px solid ${COLORS.accent};
  padding: ${SPACING.s3}px;
  background: ${COLORS.white};
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};

`;

const AMOUNT_CHANGE_CALLOUT_LABEL_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_LABEL_STYLE;
  color: ${COLORS.textTertiary};
  text-transform: uppercase;
`;

const AMOUNT_CHANGE_CALLOUT_TITLE_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_TITLE_STYLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: normal;
  line-height: 1.3;
`;

const AMOUNT_CHANGE_CALLOUT_VALUES_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_VALUES_STYLE;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${SPACING.s2}px;
`;

const AMOUNT_CHANGE_CALLOUT_ORIGINAL_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_ORIGINAL_STYLE;
  text-decoration: line-through;
`;

const AMOUNT_CHANGE_CALLOUT_UPDATED_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_UPDATED_STYLE;
  color: ${COLORS.ink};
`;

const AMOUNT_CHANGE_CALLOUT_DESCRIPTION_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_DESCRIPTION_STYLE;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.regular};
  letter-spacing: normal;
  line-height: 1.35;
`;

const AMOUNT_CHANGE_CALLOUT_CANCEL_STYLE = css`
  label: AMOUNT_CHANGE_CALLOUT_CANCEL_STYLE;
  border: 1px solid ${COLORS.stroke};
  padding: ${SPACING.s1}px ${SPACING.s2}px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.ink};
    background: ${COLORS.accent};
  }
`;

const DETAIL_ROW_STYLE = css`
  label: DETAIL_ROW_STYLE;
  td {
    padding: ${SPACING.s3}px ${SPACING.s5}px ${SPACING.s5}px;
    border-bottom: 1px solid ${COLORS.stroke};
  }
`;

const DETAIL_CONTENT_STYLE = css`
  label: DETAIL_CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s3}px;
  padding-left: calc(96px + ${SPACING.s3}px);
`;

const BADGE_ROW_STYLE = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.s2}px;
`;

const BADGE_STYLE = css`
  border: 1px solid ${COLORS.stroke};
  padding: 2px ${SPACING.s2}px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const PRODUCT_LINE_STYLE = css`
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.45;
`;

const DETAIL_LABEL_STYLE = css`
  margin-right: ${SPACING.s2}px;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const NOTES_STYLE = css`
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.5;
`;

const PHOTO_ROW_STYLE = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.s2}px;
`;

const PHOTO_BUTTON_STYLE = css`
  label: PHOTO_BUTTON_STYLE;
  width: 86px;
  height: 58px;
  padding: 0;
  border: 1px solid ${COLORS.stroke};
  cursor: zoom-in;

  &:hover {
    border-color: ${COLORS.ink};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ink};
    outline-offset: 2px;
  }
`;

const PHOTO_STYLE = css`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PHOTO_GALLERY_STYLE = css`
  label: PHOTO_GALLERY_STYLE;
  display: grid;
  gap: ${SPACING.s6}px;
  padding: ${SPACING.s5}px;

  @media (max-width: 760px) {
    gap: ${SPACING.s5}px;
    padding: ${SPACING.s4}px;
  }
`;

const PHOTO_GALLERY_GROUP_STYLE = css`
  label: PHOTO_GALLERY_GROUP_STYLE;
  display: grid;
  gap: ${SPACING.s4}px;
`;

const PHOTO_GALLERY_GROUP_TITLE_STYLE = css`
  label: PHOTO_GALLERY_GROUP_TITLE_STYLE;
  border-bottom: 1px solid ${COLORS.stroke};
  padding-bottom: ${SPACING.s2}px;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const PHOTO_GALLERY_TASK_STYLE = css`
  label: PHOTO_GALLERY_TASK_STYLE;
  display: grid;
  gap: ${SPACING.s2}px;
`;

const PHOTO_GALLERY_TASK_TITLE_STYLE = css`
  label: PHOTO_GALLERY_TASK_TITLE_STYLE;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const PHOTO_GALLERY_GRID_STYLE = css`
  label: PHOTO_GALLERY_GRID_STYLE;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${SPACING.s8 * 3}px, 1fr));
  gap: ${SPACING.s3}px;

  @media (max-width: 760px) {
    grid-template-columns: repeat(auto-fill, minmax(${SPACING.s8 * 2}px, 1fr));
  }
`;

const PHOTO_GALLERY_BUTTON_STYLE = css`
  label: PHOTO_GALLERY_BUTTON_STYLE;
  display: block;
  aspect-ratio: 4 / 3;
  width: 100%;
  padding: 0;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.surfaceMuted};
  overflow: hidden;
  cursor: zoom-in;

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.ink};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ink};
    outline-offset: 2px;
  }
`;

const PHOTO_MODAL_CONTENT_STYLE = css`
  label: PHOTO_MODAL_CONTENT_STYLE;
  position: relative;
  width: 100%;
  height: 100%;
  background: ${COLORS.ink};
`;

const PHOTO_MODAL_CLOSE_STYLE = css`
  label: PHOTO_MODAL_CLOSE_STYLE;
  position: absolute;
  top: ${SPACING.s3}px;
  right: ${SPACING.s3}px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${SPACING.s5}px;
  height: ${SPACING.s5}px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  color: ${COLORS.ink};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.ink};
    background: ${COLORS.surfaceMuted};
  }
`;

const EMPTY_STYLE = css`
  padding: ${SPACING.s8}px ${SPACING.s5}px;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  text-align: center;
`;

const FOOTER_STYLE = css`
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: ${SPACING.s3}px;
  padding: ${SPACING.s5}px;
`;

const FOOTER_TOTAL_STYLE = css`
  color: ${COLORS.success};
  font-family: ${FONT_FAMILY.display};
  font-size: ${pxToRem(FONT_SIZE.displayLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1;
`;

const FOOTER_DIFFERENCE_POSITIVE_STYLE = css`
  color: ${COLORS.success};
`;

const FOOTER_DIFFERENCE_NEGATIVE_STYLE = css`
  color: ${COLORS.error};
`;

const FOOTER_STRIKETHROUGH_STYLE = css`
  text-decoration: line-through;
`;

const getFooterDifferenceStyle = (totalDifference: number) => {
  if (totalDifference > 0) return FOOTER_DIFFERENCE_POSITIVE_STYLE;
  if (totalDifference < 0) return FOOTER_DIFFERENCE_NEGATIVE_STYLE;
  return undefined;
};

const getVisibleTaskPhotos = (
  photos: HistoricalArtifactTask['photos'],
  includePhotos: boolean,
) => includePhotos ? photos : [];

const getPhotoGalleryGroupsFromRooms = (
  rooms: ReturnType<typeof buildHistoricalArtifactRooms>,
): HistoricalArtifactPhotoGalleryGroup[] => {
  const photoIds = new Set<string>();
  return rooms.flatMap(room => {
    const tasks = room.tasks.flatMap(task => {
      const photos = task.photos.flatMap(photo => {
        if (photoIds.has(photo.id)) return [];
        photoIds.add(photo.id);
        return [{ dataUrl: photo.dataUrl, id: photo.id, title: task.title }];
      });
      return photos.length > 0 ? [{ id: task.id, photos, title: task.title }] : [];
    });
    return tasks.length > 0 ? [{ id: room.id, tasks, title: room.name }] : [];
  });
};

/** Parses a currency-text field into a non-negative amount while the user edits it. */
const getEditableAmount = (value: string) => {
  const amount = value.replace(/[^\d.-]/g, '');
  if (!amount) return undefined;

  const parsedAmount = Number(amount);
  return Number.isFinite(parsedAmount) && parsedAmount >= 0
    ? parsedAmount
    : undefined;
};

/** Lets audience-doc editors update a task amount in the cloned scan. */
const HistoricalArtifactTaskAmountInput = ({
  task,
  onTaskAmountCommit,
}: {
  task: HistoricalArtifactTask;
  onTaskAmountCommit?: (task: HistoricalArtifactTask, updatedAmount: number) => void;
}) => {
  const [editingValue, setEditingValue] = useState<string>();
  const amount = task.amount;

  return (
    <input
      aria-label={`Edit amount for ${task.title}`}
      css={EDITABLE_AMOUNT_INPUT_STYLE}
      inputMode='decimal'
      value={editingValue ?? formatCurrency(amount)}
      onChange={event => setEditingValue(event.target.value)}
      onFocus={event => event.currentTarget.select()}
      onBlur={() => {
        const updatedAmount = getEditableAmount(editingValue ?? formatCurrency(amount));
        if (updatedAmount !== undefined && updatedAmount !== amount) {
          onTaskAmountCommit?.(task, updatedAmount);
        }
        setEditingValue(undefined);
      }}
    />
  );
};

/** Shows how a cloned audience-doc task differs from its baseline scan amount. */
const AmountChangeCallout = ({
  baselineAmount,
  task,
  onTaskAmountCommit,
}: {
  baselineAmount: number;
  task: HistoricalArtifactTask;
  onTaskAmountCommit?: (task: HistoricalArtifactTask, updatedAmount: number) => void;
}) => (
  <div css={AMOUNT_CHANGE_CALLOUT_STYLE} aria-label={`Audience amount changed for ${task.title}`}>
    <span css={AMOUNT_CHANGE_CALLOUT_LABEL_STYLE}>Audience doc</span>
    <span css={AMOUNT_CHANGE_CALLOUT_TITLE_STYLE}>{task.title}</span>
    <div css={AMOUNT_CHANGE_CALLOUT_VALUES_STYLE}>
      <span css={AMOUNT_CHANGE_CALLOUT_ORIGINAL_STYLE}>{formatCurrency(baselineAmount)}</span>
      <span aria-hidden='true'>→</span>
      <span css={AMOUNT_CHANGE_CALLOUT_UPDATED_STYLE}>
        {formatCurrency(task.amount)}
      </span>
    </div>
    <span css={AMOUNT_CHANGE_CALLOUT_DESCRIPTION_STYLE}>
      Saved to this audience-doc copy.
    </span>
    {onTaskAmountCommit && (
      <button
        type='button'
        css={AMOUNT_CHANGE_CALLOUT_CANCEL_STYLE}
        onClick={() => onTaskAmountCommit(task, baselineAmount)}
      >
        Cancel change
      </button>
    )}
  </div>
);

const renderTaskRows = (
  task: HistoricalArtifactTask,
  {
    canEditAmounts,
    includePhotos,
    onOpenPhoto,
    onTaskAmountCommit,
    showChangeOrderDetails,
    showAmounts,
  }: {
    canEditAmounts: boolean;
    includePhotos: boolean;
    onOpenPhoto: (photo: HistoricalArtifactPhoto) => void;
    onTaskAmountCommit?: (task: HistoricalArtifactTask, updatedAmount: number) => void;
    showChangeOrderDetails: boolean;
    showAmounts: boolean;
  },
) => {
  const visiblePhotos = getVisibleTaskPhotos(task.photos, includePhotos);

  return (
    <>
      <tr key={`${task.id}-main`} css={TASK_ROW_STYLE}>
        <td css={TASK_ID_STYLE}>{kaiPartialIdPresenter(task.id)}</td>
        <td>
          <div css={TASK_TITLE_STYLE}>{task.title}</div>
          {showChangeOrderDetails && task.changeLabels.length > 0 && (
            <div css={BADGE_ROW_STYLE}>
              {task.changeLabels.map(changeLabel => (
                <span key={changeLabel} css={BADGE_STYLE}>
                  {changeLabel}
                </span>
              ))}
            </div>
          )}
          {task.taskName !== task.title && <div css={TASK_DESCRIPTION_STYLE}>Task: {task.taskName}</div>}
          {task.description && <div css={TASK_DESCRIPTION_STYLE}>{task.description}</div>}
        </td>
        <td
          css={[
            CONTRACTOR_STYLE,
            task.contractorName === 'Unassigned' && UNASSIGNED_CONTRACTOR_STYLE,
          ]}
        >
          {showChangeOrderDetails && task.baselineContractorName && <span css={PREVIOUS_AMOUNT_STYLE}>{task.baselineContractorName}</span>}
          {task.contractorName}
        </td>
        <td css={[METRIC_STYLE, NUMERIC_COLUMN_STYLE]}>
          {showChangeOrderDetails && task.baselineQuantityLabel && <span css={PREVIOUS_AMOUNT_STYLE}>{task.baselineQuantityLabel}</span>}
          {task.quantityLabel}
        </td>
        {showAmounts && (
          <>
            <td css={[METRIC_STYLE, NUMERIC_COLUMN_STYLE]}>{task.rateLabel}</td>
            <td css={[AMOUNT_STYLE, NUMERIC_COLUMN_STYLE]}>
              {canEditAmounts && onTaskAmountCommit
                ? (
                  <HistoricalArtifactTaskAmountInput
                    task={task}
                    onTaskAmountCommit={onTaskAmountCommit}
                  />
                )
                : (
                  <>
                    {showChangeOrderDetails && task.baselineAmount !== task.amount && (
                      <span css={PREVIOUS_AMOUNT_STYLE}>{formatCurrency(task.baselineAmount)}</span>
                    )}
                    {formatCurrency(task.amount)}
                  </>
                )}
            </td>
          </>
        )}
      </tr>
      <tr key={`${task.id}-detail`} css={DETAIL_ROW_STYLE}>
        <td colSpan={showAmounts ? 6 : 4}>
          <div css={DETAIL_CONTENT_STYLE}>
            {task.badges.length > 0 && (
              <div css={BADGE_ROW_STYLE}>
                {task.badges.map(badge => (
                  <span key={badge} css={BADGE_STYLE}>
                    {badge}
                  </span>
                ))}
              </div>
            )}
            {task.productLines.map(productLine => (
              <div key={productLine} css={PRODUCT_LINE_STYLE}>
                <span css={DETAIL_LABEL_STYLE}>Product</span>
                {productLine}
              </div>
            ))}
            {task.noteLines.length > 0 && (
              <div css={NOTES_STYLE}>
                {task.noteLines.map(note => <div key={note}>{note}</div>)}
              </div>
            )}
            {visiblePhotos.length > 0 && (
              <div css={PHOTO_ROW_STYLE}>
                {visiblePhotos.map(photo => (
                  <button
                    key={photo.id}
                    type='button'
                    css={PHOTO_BUTTON_STYLE}
                    aria-label={`View larger image for ${task.title}`}
                    onClick={() =>
                      onOpenPhoto({
                        dataUrl: photo.dataUrl,
                        id: photo.id,
                        title: task.title,
                      })}
                  >
                    <img css={PHOTO_STYLE} src={photo.dataUrl} alt={task.title} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

const getArtifactReportType = (documentType: HistoricalArtifactDocumentType): ReportType => documentType;

/** Places purchase-order, PDF, and photo ZIP downloads beside the document's print control. */
const HistoricalArtifactDocumentExportActions = ({
  scan,
  documentType,
  photoCount,
  onDownloadPhotosZip,
}: {
  scan: ApiScan;
  documentType: HistoricalArtifactDocumentType;
  photoCount: number;
  onDownloadPhotosZip?: () => void;
}) => {
  const reportType = getArtifactReportType(documentType);
  const excelExportOptions = {
    exportHook: useScanExcelExport,
    scanId: scan.id,
    reportType,
    kind: 'Excel',
    eventName: ANALYTICS_EVENTS.scanExportedExcel,
  };
  const pdfExportOptions = {
    exportHook: useScanPdfExport,
    scanId: scan.id,
    reportType,
    kind: 'PDF',
    eventName: ANALYTICS_EVENTS.scanExportedPdf,
  };
  const { prepareReport: prepareExcelReport, isLoading: isExportingExcel } = useScopeReportDownloader(excelExportOptions);
  const { prepareReport: preparePdfReport, isLoading: isExportingPdf } = useScopeReportDownloader(pdfExportOptions);
  const { closeMenu, containerRef, open, toggleMenu } = useDropdownMenu();

  return (
    <div css={TOOLBAR_EXPORT_CONTAINER_STYLE} ref={containerRef}>
      <button
        type='button'
        css={TOOLBAR_PRINT_STYLE}
        aria-expanded={open}
        aria-haspopup='menu'
        onClick={toggleMenu}
      >
        Export
      </button>
      {open && (
        <div css={TOOLBAR_EXPORT_MENU_STYLE} role='menu'>
          <button
            type='button'
            role='menuitem'
            css={TOOLBAR_EXPORT_MENU_ITEM_STYLE}
            disabled={isExportingExcel}
            onClick={() => {
              prepareExcelReport();
              closeMenu();
            }}
          >
            {isExportingExcel ? 'Exporting purchase orders...' : 'Export purchase orders (Excel)'}
          </button>
          <button
            type='button'
            role='menuitem'
            css={TOOLBAR_EXPORT_MENU_ITEM_STYLE}
            disabled={isExportingPdf}
            onClick={() => {
              preparePdfReport();
              closeMenu();
            }}
          >
            {isExportingPdf ? 'Exporting PDF...' : 'Download PDF'}
          </button>
          {onDownloadPhotosZip && (
            <button
              type='button'
              role='menuitem'
              css={TOOLBAR_EXPORT_MENU_ITEM_STYLE}
              onClick={() => {
                onDownloadPhotosZip();
                closeMenu();
              }}
            >
              Download {pluralize(photoCount, 'Photo')} (ZIP)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** Historical artifact document, including before/after values for editable audience-doc copies. */
export const HistoricalScopeArtifactDocument = ({
  artifactTitle,
  documentType = 'fullScope',
  scan,
  onBack,
  backLabel = 'All artifacts',
  emptyText = 'This artifact does not contain any scoped items.',
  filterStrip,
  includePhotos = true,
  photosOnly = false,
  photoGalleryGroups,
  scopeLineItems,
  showAmounts = true,
  baselineTaskAmounts,
  canEditAmounts = false,
  onTaskAmountCommit,
  toolbarMeta,
  toolbarStatus,
  photoCount: loadedPhotoCount,
  onDownloadPhotosZip,
  showPurchaseOrderExports = false,
}: {
  artifactTitle: string;
  documentType?: HistoricalArtifactDocumentType;
  scan: ApiScan;
  onBack: () => void;
  backLabel?: string;
  emptyText?: string;
  filterStrip?: ReactNode;
  includePhotos?: boolean;
  photosOnly?: boolean;
  photoGalleryGroups?: HistoricalArtifactPhotoGalleryGroup[];
  scopeLineItems?: ScopeReportLineItem[];
  showAmounts?: boolean;
  baselineTaskAmounts?: Map<string, number>;
  canEditAmounts?: boolean;
  onTaskAmountCommit?: (task: HistoricalArtifactTask, updatedAmount: number) => void;
  toolbarMeta?: ReactNode;
  toolbarStatus?: ReactNode;
  /** Omitted while the photo count hasn't loaded yet — same gate as the export menu's photo ZIP action. */
  photoCount?: number;
  /** Omitted (no photo ZIP menu item) for a host with nothing to download. */
  onDownloadPhotosZip?: () => void;
  /** Keeps purchase-order exports on approved artifacts, never drafts or audience-document copies. */
  showPurchaseOrderExports?: boolean;
}) => {
  const { artifactDocumentRef, printArtifactDocument } = useArtifactDocumentPrint();
  const [selectedPhoto, setSelectedPhoto] = useState<HistoricalArtifactPhoto>();
  const { reportData, diffReportData, isLoading, isError } = useScopeReportData();
  const isChangeOrderDocument = documentType === 'changeOrder';
  const isCloseoutDocument = documentType === 'closeout';
  const artifactKind = isCloseoutDocument
    ? 'Closeout'
    : scan.scanType === 'changeRequest'
    ? 'Change order'
    : 'Original scope';
  const documentScopeLineItems = scopeLineItems
    ?? (isChangeOrderDocument
      ? getReportData({
        diffScopeLineItems: diffReportData?.scopeLineItems,
        reportType: 'changeOrder',
      }).scopeLineItems
      : reportData?.scopeLineItems);
  const modifierLabels = useTaskModifierDisplayNames();
  const rooms = useMemo(
    () =>
      buildHistoricalArtifactRooms(
        documentScopeLineItems ?? [],
        modifierLabels,
      ),
    [documentScopeLineItems, modifierLabels],
  );
  const overview = reportData?.overviewData;
  const scopeNotes = overview?.scopeNotes ?? [];
  const preparedAt = scan.approvedAt ?? scan.createdAt;
  const defaultPhotoGalleryGroups = useMemo(
    () => getPhotoGalleryGroupsFromRooms(rooms),
    [rooms],
  );
  const visiblePhotoGalleryGroups = photoGalleryGroups ?? defaultPhotoGalleryGroups;
  const galleryPhotoCount = visiblePhotoGalleryGroups.reduce(
    (count, group) =>
      count + group.tasks.reduce(
        (taskCount, task) => taskCount + task.photos.length,
        0,
      ),
    0,
  );
  const tasksWithAmountChanges = rooms
    .flatMap(room => room.tasks)
    .flatMap(task => {
      const baselineAmount = baselineTaskAmounts?.get(task.id);
      return baselineAmount !== undefined && baselineAmount !== task.amount
        ? [{ baselineAmount, task }]
        : [];
    });
  const hasAmountChangeCallouts = !photosOnly && canEditAmounts && tasksWithAmountChanges.length > 0;
  const changeOrderOverview = diffReportData?.overviewData;
  const basedOnScopeVersionId = scan.id ?? scan.parentScanId;
  const documentTotal = scopeLineItems
    ? rooms.reduce((totalAmount, room) => totalAmount + room.totalAmount, 0)
    : (overview?.totalProjectCost
      ?? (scan.totalLaborCost ?? 0) + (scan.totalMaterialCost ?? 0));

  return (
    <div css={VIEWPORT_STYLE}>
      <div
        css={[
          DOCUMENT_FRAME_STYLE,
          hasAmountChangeCallouts
            ? DOCUMENT_FRAME_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE
            : undefined,
        ]}
      >
        <section
          ref={artifactDocumentRef}
          css={[
            DOCUMENT_STYLE,
            hasAmountChangeCallouts
              ? DOCUMENT_WITH_AMOUNT_CHANGE_CALLOUTS_STYLE
              : undefined,
          ]}
          aria-label={`${artifactTitle} historical artifact document`}
        >
          <div css={TOOLBAR_STYLE}>
            <button type='button' css={TOOLBAR_BACK_STYLE} onClick={onBack}>
              ‹ {backLabel}
            </button>
            <span css={TOOLBAR_DIVIDER_STYLE} aria-hidden='true' />
            <span css={TOOLBAR_TITLE_STYLE}>{artifactTitle}</span>
            {toolbarMeta ?? (
              <span css={TOOLBAR_META_STYLE}>
                {artifactKind} · {scan.scanLifecycleStatus === 'approved'
                  ? `Approved ${formatArtifactDate(preparedAt)}`
                  : `Working copy · started ${formatArtifactDate(scan.createdAt)}`}
              </span>
            )}
            <div css={TOOLBAR_ACTIONS_STYLE}>
              <button
                type='button'
                css={TOOLBAR_PRINT_STYLE}
                onClick={printArtifactDocument}
              >
                Print
              </button>
              {showPurchaseOrderExports && (
                <HistoricalArtifactDocumentExportActions
                  scan={scan}
                  documentType={documentType}
                  photoCount={loadedPhotoCount ?? galleryPhotoCount}
                  onDownloadPhotosZip={onDownloadPhotosZip}
                />
              )}
              <div css={TOOLBAR_STATUS_STYLE}>
                {toolbarStatus ?? (
                  <span css={BADGE_STYLE}>
                    {isCloseoutDocument ? 'Approved' : artifactKind}
                  </span>
                )}
              </div>
            </div>
          </div>

          {filterStrip}

          <header css={HEADER_STYLE}>
            <div>
              <div css={EYEBROW_STYLE}>
                KAI · {photosOnly ? 'Photos' : isChangeOrderDocument ? 'Change order' : isCloseoutDocument ? 'Closeout' : 'Scope of work'} · {artifactTitle}
              </div>
              <div css={DOCUMENT_TITLE_STYLE}>
                {overview?.propertyAddress || scan.name || 'Scope of Work'}
              </div>
              <div css={DOCUMENT_SUBTITLE_STYLE}>
                {overview?.projectName ? `Project: ${overview.projectName}` : artifactTitle}
              </div>
            </div>
            <div css={DOCUMENT_META_STYLE}>
              <div>
                <div css={DOCUMENT_META_LABEL_STYLE}>Property ID</div>
                <div css={DOCUMENT_META_VALUE_STYLE}>{overview?.propertyId || '—'}</div>
              </div>
              <div>
                <div css={DOCUMENT_META_LABEL_STYLE}>
                  {scan.scanType === 'audienceDoc' ? 'Audience doc' : 'Artifact'}
                </div>
                <div css={DOCUMENT_META_VALUE_STYLE}>{artifactTitle}</div>
              </div>
              <div>
                <div css={DOCUMENT_META_LABEL_STYLE}>Prepared</div>
                <div css={DOCUMENT_META_VALUE_STYLE}>
                  {formatArtifactDate(preparedAt)}
                </div>
              </div>
              <div>
                <div css={DOCUMENT_META_LABEL_STYLE}>Based on</div>
                <div css={DOCUMENT_META_VALUE_STYLE}>
                  {photosOnly
                    ? 'Photo gallery'
                    : `Scope ${kaiPartialIdPresenter(basedOnScopeVersionId)}`}
                </div>
              </div>
            </div>
          </header>

          {!isLoading && !isError && !photosOnly && scopeNotes.length > 0 && (
            <section css={SCOPE_NOTES_STYLE} aria-label='Scope notes'>
              <div css={EYEBROW_STYLE}>Scope notes</div>
              {scopeNotes.map((note, index) => <div key={index}>{note}</div>)}
            </section>
          )}
          {isLoading && <div css={EMPTY_STYLE}>Loading historical scope...</div>}
          {!isLoading && isError && (
            <div css={EMPTY_STYLE}>
              This historical scope could not be loaded.
            </div>
          )}
          {!isLoading && !isError && (photosOnly ? galleryPhotoCount === 0 : rooms.length === 0) && (
            <div css={EMPTY_STYLE}>
              {photosOnly ? 'This audience document does not have task photos.' : emptyText}
            </div>
          )}
          {!isLoading && !isError && photosOnly && galleryPhotoCount > 0 && (
            <div css={PHOTO_GALLERY_STYLE} aria-label='Audience document photos'>
              {visiblePhotoGalleryGroups.map(group => (
                <section key={group.id} css={PHOTO_GALLERY_GROUP_STYLE}>
                  <div css={PHOTO_GALLERY_GROUP_TITLE_STYLE}>{group.title}</div>
                  {group.tasks.map(task => (
                    <section key={task.id} css={PHOTO_GALLERY_TASK_STYLE}>
                      <div css={PHOTO_GALLERY_TASK_TITLE_STYLE}>{task.title}</div>
                      <div css={PHOTO_GALLERY_GRID_STYLE}>
                        {task.photos.map(photo => (
                          <button
                            key={photo.id}
                            type='button'
                            css={PHOTO_GALLERY_BUTTON_STYLE}
                            aria-label={`View larger photo for ${photo.title}`}
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img css={PHOTO_STYLE} src={photo.dataUrl} alt={photo.title} loading='lazy' />
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </section>
              ))}
            </div>
          )}
          {!isLoading && !isError && !photosOnly && rooms.length > 0 && (
            <div css={TABLE_WRAP_STYLE}>
              <table css={TABLE_STYLE}>
                <colgroup>
                  <col css={ID_COLUMN_STYLE} />
                  <col css={ITEM_COLUMN_STYLE} />
                  <col css={CONTRACTOR_COLUMN_STYLE} />
                  <col css={QUANTITY_COLUMN_STYLE} />
                  {showAmounts && (
                    <>
                      <col css={RATE_COLUMN_STYLE} />
                      <col css={AMOUNT_COLUMN_STYLE} />
                    </>
                  )}
                </colgroup>
                <thead>
                  <tr>
                    <th css={ID_COLUMN_STYLE}>ID</th>
                    <th>Item</th>
                    <th>Contractor</th>
                    <th css={[QUANTITY_COLUMN_STYLE, NUMERIC_COLUMN_STYLE]}>
                      Qty
                    </th>
                    {showAmounts && (
                      <>
                        <th css={[RATE_COLUMN_STYLE, NUMERIC_COLUMN_STYLE]}>
                          Rate
                        </th>
                        <th css={[AMOUNT_COLUMN_STYLE, NUMERIC_COLUMN_STYLE]}>
                          Amount
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                {rooms.map(room => (
                  <tbody key={room.id}>
                    <tr css={GROUP_ROW_STYLE}>
                      <td colSpan={showAmounts ? 6 : 4}>
                        <span css={GROUP_NAME_STYLE}>{room.name}</span>
                        <span css={GROUP_META_STYLE}>
                          · {room.tasks.length} item
                          {room.tasks.length === 1 ? '' : 's'}
                          {showAmounts && <>· {formatCurrency(room.totalAmount)}</>}
                        </span>
                      </td>
                    </tr>
                    {room.noteLines.length > 0 && (
                      <tr css={GROUP_NOTE_ROW_STYLE}>
                        <td colSpan={showAmounts ? 6 : 4}>
                          {room.noteLines.join(' ')}
                        </td>
                      </tr>
                    )}
                    {room.tasks.map(task =>
                      renderTaskRows(task, {
                        canEditAmounts,
                        includePhotos,
                        onOpenPhoto: setSelectedPhoto,
                        onTaskAmountCommit,
                        showChangeOrderDetails: isChangeOrderDocument,
                        showAmounts,
                      })
                    )}
                  </tbody>
                ))}
              </table>
            </div>
          )}

          {!photosOnly && showAmounts && (
            <footer css={FOOTER_STYLE}>
              {isChangeOrderDocument && changeOrderOverview
                ? (
                  <span css={EYEBROW_STYLE}>
                    <span css={FOOTER_STRIKETHROUGH_STYLE}>
                      Last Total Version: {formatCurrency(changeOrderOverview.baselineTotalCost || 0)}
                    </span>
                    {' · '}
                    Difference:{' '}
                    <span css={getFooterDifferenceStyle(changeOrderOverview.totalDifference || 0)}>
                      {changeOrderOverview.totalDifference > 0 ? '+' : ''}
                      {formatCurrency(changeOrderOverview.totalDifference || 0)}
                    </span>
                    {' · '}
                    New Total Amount:
                  </span>
                )
                : <span css={EYEBROW_STYLE}>Document total</span>}
              <span css={FOOTER_TOTAL_STYLE}>
                {formatCurrency(
                  isChangeOrderDocument && changeOrderOverview
                    ? (overview?.totalProjectCost || 0)
                    : documentTotal,
                )}
              </span>
            </footer>
          )}
        </section>
        {hasAmountChangeCallouts && (
          <aside css={AMOUNT_CHANGE_CALLOUT_RAIL_STYLE}>
            {tasksWithAmountChanges.map(({ baselineAmount, task }) => (
              <AmountChangeCallout
                key={task.id}
                baselineAmount={baselineAmount}
                task={task}
                onTaskAmountCommit={onTaskAmountCommit}
              />
            ))}
          </aside>
        )}
      </div>
      {selectedPhoto && (
        <GalleryPhotoModal onClose={() => setSelectedPhoto(undefined)}>
          <div css={PHOTO_MODAL_CONTENT_STYLE}>
            <ZoomableImageViewer
              key={selectedPhoto.id}
              imageUri={selectedPhoto.dataUrl}
            />
            <button
              type='button'
              css={PHOTO_MODAL_CLOSE_STYLE}
              aria-label='Close enlarged image'
              onClick={() => setSelectedPhoto(undefined)}
            >
              <CloseIcon size={16} color='currentColor' aria-hidden='true' />
            </button>
          </div>
        </GalleryPhotoModal>
      )}
    </div>
  );
};
